/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { TokenManager } from "../../../model/auth";
import { DeviceModel } from "../../../model/device";
import { getServiceErrorCode } from "../../../model/error";
import { Log } from "../../../model/logging";
import {
  SignInFlow,
  SignInFlowEvent,
  SignInLogger,
  type SignInFlowName
} from "../../../model/logging/SignInLogger";
import { DiagnosticSeverity } from "../../../model/types";
import { reloadApplicationAtRoot } from "../../../model/utility/reloadApplicationAtRoot";

const TAG: string = "signIn/deviceIdentityRecovery";

/**
 * The DPS body code for a device logging id that FE has permanently removed
 * (HTTP 410). The schema's recovery: regenerate the DLID and key pair, then set
 * the device up again.
 */
export const DLID_PERMANENTLY_REMOVED_SERVICE_CODE: string =
  "AUTHS.DLID_PERMANENTLY_REMOVED";

/**
 * Whether a failed `v2/provisionDevices` call means this device's identity was
 * removed server-side. Read from the backend body code the interactor kept
 * (`attachHttpContext`), since the catalog code is still the A477 catch-all.
 */
export function isDeviceIdentityRemovedError(error: unknown): boolean {
  return getServiceErrorCode(error) === DLID_PERMANENTLY_REMOVED_SERVICE_CODE;
}

/**
 * Replaces the removed identity with a new one and drops every token minted
 * for the old one, so the next sign-in attempt starts clean: its claim, bind
 * and provisioning all carry the new DLID.
 *
 * Never throws. A failure to regenerate is logged and reported as `false`; the
 * caller still surfaces the sign-in failure, and the next attempt will hit the
 * same 410 and try again.
 *
 * @param flow - The sign-in flow that received the 410, for diagnostics.
 * @returns whether a new identity is in place.
 */
export async function recoverRemovedDeviceIdentity(
  flow: SignInFlowName
): Promise<boolean> {
  try {
    await DeviceModel.resetDeviceIdentity();
    await TokenManager.clear();
    SignInLogger.logFlowEvent(
      SignInFlowEvent.DeviceIdentityReset,
      "Device identity regenerated after DPS reported it removed",
      DiagnosticSeverity.Warning,
      { flow: flow, outcome: "regenerated" }
    );
    return true;
  } catch (error: unknown) {
    Log.e(TAG, "recoverRemovedDeviceIdentity() failed", error);
    SignInLogger.logFlowEvent(
      SignInFlowEvent.DeviceIdentityReset,
      "Device identity could not be regenerated",
      DiagnosticSeverity.Error,
      { flow: flow, outcome: "failed" }
    );
    return false;
  }
}

/**
 * The 410 recovery once the user is signed in — reached from the post-sign-in
 * DPS re-run, not from a sign-in flow.
 *
 * There is no flow here to re-run: the session was built on the removed
 * identity, and the new one is not bound to the account yet, so the user has
 * to sign in again. Tokens are cleared even when regeneration fails, so the
 * restart cannot rehydrate the dead session; the sign-in attempt that follows
 * then meets the 410 itself and retries the regeneration there.
 *
 * Restarts at `/` rather than navigating in place: every slice still holds
 * state from the removed session, and with no token the splash screen routes
 * straight to sign-in.
 */
export async function recoverRemovedDeviceIdentityAfterSignIn(): Promise<void> {
  const regenerated: boolean = await recoverRemovedDeviceIdentity(
    SignInFlow.Reprovision
  );
  if (!regenerated) {
    await TokenManager.clear();
  }
  reloadApplicationAtRoot();
}
